"use client"

import * as React from "react"
import { toast } from "sonner"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Input,
  Label,
  Select,
  Textarea,
} from "@ibada/ui"
import { FinancePaymentMethod } from "@ibada/shared-types/finance"

import { useTranslations } from "@/i18n"
import { useModulePermission } from "@/features/auth/hooks/use-module-permission"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"
import type { FinanceTransactionRecord } from "@/features/finance/model/finance-types"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import {
  MobileFormInputRow,
  MobileFormSection,
  MobileFormSelectRow,
  MobileFormSheet,
  MobileFormTextArea,
} from "@/shared/components/mobile-form"

/** The subset of a transaction this dialog can change. Everything financial is immutable server-side. */
export interface EditableTransactionMetadata {
  id: string
  description: string | null
  paymentMethod: FinancePaymentMethod | null
  paymentReference: string | null
}

const NONE = "__none__"

/**
 * Edit the non-financial details of a posted transaction.
 *
 * Deliberately narrow: description, payment method, payment reference. Amounts, accounts, fund, date and
 * branch are immutable — correcting one of those is still a cancel plus a reversal, because the ledger records
 * what happened rather than what we currently believe. A mistyped cheque number is not that, and forcing a
 * reversal for it would put a correction in the books that misrepresents the event.
 *
 * The API enforces all of this (409 `TRANSACTION_IMMUTABLE`); this dialog simply does not offer the rest.
 */
export function TransactionMetadataEditDialog({
  transaction,
  open,
  onOpenChange,
  onSaved,
}: {
  transaction: EditableTransactionMetadata | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: (transaction: FinanceTransactionRecord) => void
}) {
  const t = useTranslations("finance.transactions")
  const { canEdit } = useModulePermission(WorkspaceAccessModule.FINANCE)
  const toastError = useApiErrorToast("finance")
  const { useFinanceUpdateTransactionMetadataMutation } = useFinanceFeatureQuery()
  const update = useFinanceUpdateTransactionMetadataMutation()
  const isMobile = useIsMobile()

  const [description, setDescription] = React.useState("")
  const [paymentMethod, setPaymentMethod] = React.useState<string>(NONE)
  const [paymentReference, setPaymentReference] = React.useState("")

  // Reload from the record each time it opens, so a cancelled edit never leaks into the next one.
  React.useEffect(() => {
    if (!open || !transaction) return
    setDescription(transaction.description ?? "")
    setPaymentMethod(transaction.paymentMethod ?? NONE)
    setPaymentReference(transaction.paymentReference ?? "")
  }, [open, transaction])

  if (!transaction || !canEdit) return null

  const handleSave = async () => {
    if (!canEdit) return
    try {
      const savedTransaction = await update.mutateAsync({
        id: transaction.id,
        // Empty means "clear this field", which is a real intent — hence null rather than omitting the key.
        description: description.trim() || null,
        paymentMethod: paymentMethod === NONE ? null : (paymentMethod as FinancePaymentMethod),
        paymentReference: paymentReference.trim() || null,
      })
      onSaved?.(savedTransaction)
      toast.success(t("edit.saved"))
      onOpenChange(false)
    } catch (error) {
      toastError(error)
    }
  }

  const paymentMethodOptions = Object.values(FinancePaymentMethod).map((method) => ({
    value: method as string,
    label: t(`paymentMethod.${method}` as never),
  }))

  // Phone: the same three fields as the kit's grouped rows in a bottom sheet; the immutability rule becomes a
  // short note under them instead of a paragraph before them.
  if (isMobile) {
    return (
      <MobileFormSheet
        open={open}
        onOpenChange={onOpenChange}
        title={t("edit.title")}
        note={t("edit.description")}
        cancelLabel={t("edit.cancel")}
        saveLabel={t("edit.save")}
        onSave={() => void handleSave()}
        saving={update.isPending}
      >
        <MobileFormSection title={t("form.fields.description")}>
          <MobileFormTextArea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t("form.fields.description")}
            aria-label={t("form.fields.description")}
          />
        </MobileFormSection>
        <MobileFormSection>
          <MobileFormSelectRow
            label={t("form.fields.paymentMethod")}
            placeholder={t("edit.noPaymentMethod")}
            value={paymentMethod === NONE ? null : paymentMethod}
            onChange={(value) => setPaymentMethod(value ?? NONE)}
            options={paymentMethodOptions}
            clearLabel={t("edit.noPaymentMethod")}
            doneLabel={t("form.mobile.done")}
            emptyLabel={t("form.mobile.noResults")}
            searchPlaceholder={t("form.fields.paymentMethod")}
          />
          <MobileFormInputRow
            label={t("form.fields.paymentReference")}
            value={paymentReference}
            onChange={(event) => setPaymentReference(event.target.value)}
            placeholder={t("form.mobile.referencePlaceholder")}
          />
        </MobileFormSection>
      </MobileFormSheet>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size='md'>
        <DialogTitle>{t("edit.title")}</DialogTitle>
        <DialogDescription>{t("edit.description")}</DialogDescription>

        <div className='space-y-3'>
          <div className='space-y-1.5'>
            <Label htmlFor='tx-edit-description'>{t("form.fields.description")}</Label>
            <Textarea
              id='tx-edit-description'
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='tx-edit-payment-method'>{t("form.fields.paymentMethod")}</Label>
            <Select
              id='tx-edit-payment-method'
              className='w-full'
              value={paymentMethod}
              onValueChange={setPaymentMethod}
              placeholder={t("form.placeholders.paymentMethod")}
              options={[
                { value: NONE, label: t("edit.noPaymentMethod") },
                ...Object.values(FinancePaymentMethod).map((method) => ({
                  value: method as string,
                  label: t(`paymentMethod.${method}` as never),
                })),
              ]}
            />
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='tx-edit-payment-reference'>{t("form.fields.paymentReference")}</Label>
            <Input
              id='tx-edit-payment-reference'
              value={paymentReference}
              onChange={(event) => setPaymentReference(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter className='gap-2'>
          <Button size='sm' variant='outline' onClick={() => onOpenChange(false)} disabled={update.isPending}>
            {t("edit.cancel")}
          </Button>
          <Button size='sm' onClick={() => void handleSave()} disabled={update.isPending}>
            {t("edit.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
